// src/components/AdminJourneyUsers.js
import React, { useState, useMemo } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

function AdminJourneyUsers({
  users,
  selectedUser,
  setSelectedUser,
  handleUserInputChange,
  handleSaveUser,
  handleEditUser,
  handleDeleteUser,
  currencies = [], // New prop for currencies
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('firstName');
  const [sortDirection, setSortDirection] = useState('asc');
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  const handlePhoneChange = (value) => {
    setSelectedUser((prev) => ({
      ...prev,
      phoneNumber: value || '',
    }));
  };

  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users.filter(user => {
      const searchLower = searchTerm.toLowerCase();
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
      
      return fullName.includes(searchLower) ||
             (user.email && user.email.toLowerCase().includes(searchLower)) ||
             (user.phoneNumber && user.phoneNumber.toLowerCase().includes(searchLower)) ||
             (user.nationality && user.nationality.toLowerCase().includes(searchLower)) ||
             (user.passportId && user.passportId.toLowerCase().includes(searchLower));
    });

    return filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'firstName':
          aValue = (a.firstName || '').toLowerCase();
          bValue = (b.firstName || '').toLowerCase();
          break;
        case 'lastName':
          aValue = (a.lastName || '').toLowerCase();
          bValue = (b.lastName || '').toLowerCase();
          break;
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'gender':
          aValue = a.gender || '';
          bValue = b.gender || '';
          break;
        case 'nationality':
          aValue = (a.nationality || '').toLowerCase();
          bValue = (b.nationality || '').toLowerCase();
          break;
        case 'preferredCurrency':
          aValue = a.preferredCurrency || 'USD';
          bValue = b.preferredCurrency || 'USD';
          break;
        case 'isCreator':
          aValue = a.isCreator ? 1 : 0;
          bValue = b.isCreator ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [users, searchTerm, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return (
    <div style={{ marginBottom: '30px' }}>
      <h3>Manage Users</h3>

      {/* Form to Edit/Add a user */}
      <div>
        <label>
          First Name:
          <input
            type="text"
            name="firstName"
            value={selectedUser.firstName}
            onChange={handleUserInputChange}
          />
        </label>

        <label>
          Last Name:
          <input
            type="text"
            name="lastName"
            value={selectedUser.lastName}
            onChange={handleUserInputChange}
          />
        </label>

        {/* Gender */}
        <label>
          Gender:
          <select
            name="gender"
            value={selectedUser.gender}
            onChange={handleUserInputChange}
          >
            <option value="">(Select)</option>
            <option value="man">Man</option>
            <option value="woman">Woman</option>
          </select>
        </label>

        <label>
          Email:
          <input
            type="email"
            name="email"
            value={selectedUser.email}
            onChange={handleUserInputChange}
          />
        </label>

        {/* Phone input */}
        <label>
          Phone:
          <PhoneInput
            defaultCountry="US"
            placeholder="Enter phone number"
            value={selectedUser.phoneNumber}
            onChange={handlePhoneChange}
          />
        </label>

        {/* Currency Preference - NEW */}
        <label>
          Preferred Currency:
          <select
            name="preferredCurrency"
            value={selectedUser.preferredCurrency || 'USD'}
            onChange={handleUserInputChange}
            style={{
              padding: '5px',
              marginLeft: '10px',
              borderRadius: '4px',
            }}
          >
            {currencies.map(currency => (
              <option key={currency.code} value={currency.code}>
                {currency.code} - {currency.name}
              </option>
            ))}
          </select>
        </label>

        {/* Max Routes for Creators */}
        {selectedUser.isCreator && (
          <label>
            Max Routes Allowed:
            <input
              type="number"
              name="maxRoutes"
              value={selectedUser.maxRoutes || 1}
              onChange={handleUserInputChange}
              min="1"
              max="10"
              style={{
                padding: '5px',
                marginLeft: '10px',
                borderRadius: '4px',
                width: '60px'
              }}
            />
            <span style={{ fontSize: '0.85em', marginLeft: '8px', color: '#666' }}>
              (Default: 1 route)
            </span>
          </label>
        )}

        <label>
          Nationality:
          <input
            type="text"
            name="nationality"
            value={selectedUser.nationality}
            onChange={handleUserInputChange}
          />
        </label>

        {/* Creator Status Toggle */}
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <input
            type="checkbox"
            name="isCreator"
            checked={selectedUser.isCreator || false}
            onChange={(e) => setSelectedUser(prev => ({ ...prev, isCreator: e.target.checked }))}
            style={{ width: 'auto' }}
          />
          <span style={{ fontWeight: 'bold' }}>Creator Status</span>
        </label>

        <label>
          Passport ID:
          <input
            type="text"
            name="passportId"
            value={selectedUser.passportId}
            onChange={handleUserInputChange}
          />
        </label>

        <label>
          Passport URL:
          <input
            type="text"
            name="passportURL"
            value={selectedUser.passportURL}
            onChange={handleUserInputChange}
          />
        </label>

        <button onClick={handleSaveUser}>
          {selectedUser.id ? 'Update User' : 'Add User'}
        </button>
      </div>

      <h4 style={{
        fontSize: isMobile ? '16px' : '18px',
        marginTop: isMobile ? '20px' : '25px',
        marginBottom: isMobile ? '10px' : '15px'
      }}>Current Users</h4>
      
      {/* Search and Sort Controls */}
      <div style={{ 
        marginBottom: isMobile ? '10px' : '15px',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '8px' : '10px',
        alignItems: isMobile ? 'stretch' : 'center'
      }}>
        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: isMobile ? '6px' : '8px',
            fontSize: isMobile ? '13px' : '14px',
            border: '1px solid #ccc',
            borderRadius: '3px',
            flex: isMobile ? 'none' : '1',
            maxWidth: isMobile ? 'none' : '300px'
          }}
        />
        <div style={{ 
          fontSize: isMobile ? '12px' : '14px',
          color: '#666',
          whiteSpace: 'nowrap'
        }}>
          {filteredAndSortedUsers.length} of {users.length} users
        </div>
      </div>
      
      <div style={{ 
        overflowX: 'auto',
        marginLeft: isMobile ? '-15px' : '0',
        marginRight: isMobile ? '-15px' : '0',
        paddingLeft: isMobile ? '15px' : '0',
        paddingRight: isMobile ? '15px' : '0',
        WebkitOverflowScrolling: 'touch',
        width: isMobile ? 'calc(100% + 30px)' : '100%'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: isMobile ? '11px' : '14px',
          minWidth: isMobile ? '900px' : 'auto'
        }}>
        <thead>
          <tr>
            <th 
              onClick={() => handleSort('firstName')}
              style={{ 
                padding: isMobile ? '4px 3px' : '8px', 
                border: '1px solid #ddd', 
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Name {sortField === 'firstName' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th 
              onClick={() => handleSort('gender')}
              style={{ 
                padding: isMobile ? '4px 3px' : '8px', 
                border: '1px solid #ddd', 
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Gender {sortField === 'gender' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th 
              onClick={() => handleSort('email')}
              style={{ 
                padding: isMobile ? '4px 3px' : '8px', 
                border: '1px solid #ddd', 
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Email {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>Phone</th>
            <th 
              onClick={() => handleSort('preferredCurrency')}
              style={{ 
                padding: isMobile ? '4px 3px' : '8px', 
                border: '1px solid #ddd', 
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Currency {sortField === 'preferredCurrency' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th
              onClick={() => handleSort('nationality')}
              style={{
                padding: isMobile ? '4px 3px' : '8px',
                border: '1px solid #ddd',
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Nationality {sortField === 'nationality' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th
              onClick={() => handleSort('isCreator')}
              style={{
                padding: isMobile ? '4px 3px' : '8px',
                border: '1px solid #ddd',
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Creator {sortField === 'isCreator' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>Passport ID</th>
            <th style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>Passport URL</th>
            <th style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredAndSortedUsers.map((u) => (
            <tr key={u.id}>
              <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd' }}>
                {u.firstName} {u.lastName}
              </td>
              <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd' }}>{u.gender || ''}</td>
              <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', maxWidth: isMobile ? '100px' : '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</td>
              <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', maxWidth: isMobile ? '80px' : '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.phoneNumber}</td>
              <td style={{
                padding: isMobile ? '4px 3px' : '8px',
                border: '1px solid #ddd',
                fontWeight: 'bold',
                color: '#4CAF50'
              }}>
                {u.preferredCurrency || 'USD'}
              </td>
              <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', maxWidth: isMobile ? '80px' : '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.nationality}</td>
              <td style={{
                padding: isMobile ? '4px 3px' : '8px',
                border: '1px solid #ddd',
                textAlign: 'center',
                fontWeight: 'bold',
                color: u.isCreator ? '#10b981' : '#6b7280'
              }}>
                {u.isCreator ? 'Yes' : 'No'}
              </td>
              <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd' }}>{u.passportId}</td>
              <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd' }}>
                {u.passportURL ? (
                  <a href={u.passportURL} target="_blank" rel="noreferrer" style={{ fontSize: isMobile ? '10px' : '14px' }}>
                    View
                  </a>
                ) : (
                  'None'
                )}
              </td>
              <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', whiteSpace: 'nowrap' }}>
                <button 
                  onClick={() => handleEditUser(u)}
                  style={{
                    padding: isMobile ? '3px 5px' : '5px 8px',
                    marginRight: '4px',
                    fontSize: isMobile ? '10px' : '12px',
                    border: '1px solid #ccc',
                    borderRadius: '2px',
                    backgroundColor: '#f8f9fa',
                    cursor: 'pointer'
                  }}
                >
                  Edit
                </button>
                <button 
                  onClick={() => handleDeleteUser(u.id)}
                  style={{
                    padding: isMobile ? '3px 5px' : '5px 8px',
                    fontSize: isMobile ? '10px' : '12px',
                    border: '1px solid #dc3545',
                    borderRadius: '2px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    cursor: 'pointer'
                  }}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminJourneyUsers;